<template>
  <div class="page-invoices">
    <PageHeader>
      <div class="heading">
        <h1>
          <router-link :to="{name: 'billing'}">Billing Central</router-link> /
          Billing History
        </h1>
      </div>
    </PageHeader>

    <div class="page-columns" v-if="currentUser.owns_company">
      <div class="col main">
        <div v-if="invoicesLoaded">
          <div class="account-balance" v-if="hasBalance">
            <h3>Account Balance</h3>
            <p v-if="positiveBalance" class="positive-balance">
              Good news – Your account is in credit by <strong>{{ customerBalance }}!</strong> We'll add this to your next invoice.
            </p>
            <p v-else class="negative-balance">
              Uh oh! Your account is in negative balance by <strong>{{ customerBalance }}</strong>. Please check the invoices below to resolve this.
            </p>
            <hr />
          </div>

          <div v-if="hasInvoices">
            <h3>Upcoming Invoices</h3>
            <table-data
              :rows="upcomingInvoices"
              :headings="[
                {key: 'id', heading: 'Invoice Number'},
                {key: 'date', heading: 'Date Created'},
                {key: 'plan-status', heading: 'Status'},
                {key: 'website', heading: 'Website'},
                {key: 'amount', heading: 'Amount Due'}
              ]"
            >
              <div
                v-for="(invoice, index) in upcomingInvoices"
                :slot="'td-' + index + '-id'"
                :key="'upcoming-' + index"
              >
                XXXXXXXX-0000
              </div>

              <div
                v-for="(invoice, index) in upcomingInvoices"
                :slot="'td-' + index + '-date'"
                :key="'date-' + index"
              >
                {{ invoiceData(invoice.created) }}
              </div>

              <div
                v-for="(invoice, index) in upcomingInvoices"
                :slot="'td-' + index + '-plan-status'"
                :key="'plan-status-' + index"
              >
                <div class="row-status row-draft">
                  {{ invoice.status }}
                </div>
              </div>

              <div
                v-for="(invoice, index) in upcomingInvoices"
                :slot="'td-' + index + '-website'"
                :key="'website-' + index"
              >
                {{ invoiceSite(invoice) }}
              </div>

              <div
                v-for="(invoice, index) in upcomingInvoices"
                :slot="'td-' + index + '-amount'"
                :key="'amount-' + index"
              >
                <strong>{{ amountDue(invoice) }}</strong>
              </div>
            </table-data>

            <hr />

            <h3>Other Invoices</h3>
            <div class="invoice-links">
              <table-data
                :rows="invoices"
                :headings="[
                  {key: 'number', heading: 'Invoice Number'},
                  {key: 'date', heading: 'Date Created'},
                  {key: 'plan-status', heading: 'Status'},
                  {key: 'website', heading: 'Website'},
                  {key: 'amount', heading: 'Amount Due'},
                  {key: 'download', heading: ''},
                  {key: 'view', heading: ''}
                ]"
              >
                <div
                  v-for="(invoice, index) in invoices"
                  :slot="'td-' + index + '-date'"
                  :key="'date-' + index"
                >
                  {{ invoiceData(invoice.created) }}
                </div>

                <div
                  v-for="(invoice, index) in invoices"
                  :slot="'td-' + index + '-plan-status'"
                  :key="'plan-status-' + index"
                >
                  <div :class="`row-status row-${invoice.status}`">
                    {{ invoice.status }}
                  </div>
                </div>

                <div
                  v-for="(invoice, index) in invoices"
                  :slot="'td-' + index + '-website'"
                  :key="'website-' + index"
                >
                  {{ invoiceSite(invoice) }}
                </div>

                <div
                  v-for="(invoice, index) in invoices"
                  :slot="'td-' + index + '-download'"
                  :key="'download-' + index"
                >
                  <a :href="invoice.invoice_pdf" target="_blank">
                    <strong>Download</strong>
                  </a>
                </div>

                <div
                  v-for="(invoice, index) in invoices"
                  :slot="'td-' + index + '-amount'"
                  :key="'amount-' + index"
                >
                  <strong>{{ amountDue(invoice) }}</strong>
                </div>

                <div
                  v-for="(invoice, index) in invoices"
                  :slot="'td-' + index + '-view'"
                  :key="'view-' + index"
                >
                  <router-link
                    :to="{name: 'invoice', params: {invoiceId: invoice.id}}"
                  >
                    <strong>View</strong>
                  </router-link>
                </div>
              </table-data>
            </div>
          </div>

          <div v-else>
            <p>No invoices to show here.</p>
          </div>
        </div>

        <div v-else>
          <p>Loading Invoices...</p>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <h1>Invoices</h1>
            <p>
              Each payment has its own invoice. Invoices appear in chronological
              order, starting with your most recent payment.
            </p>
            <p>Outstanding invoices can be paid using your desired card.</p>
            <router-link class="btn is-subtle" :to="{name: 'payment-methods'}"
              >Payment Methods</router-link
            >
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view v-if="currentUser.owns_company" />
  </div>
</template>

<script>
import {mapState} from 'vuex'
import moment from 'moment'

import {
  readCustomerInvoicesData,
  readCustomerUpcomingInvoicesData
} from '@/lib/payments/invoices'

import {readStripeCustomerData} from '@/lib/payments/customers'

import page from '@/mixins/page'

import Table from '@/components/Table'
import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'

export default {
  name: 'invoices',

  data() {
    return {
      title: 'Invoices',
      positiveBalance: '',
      hasBalance: false,
      invoicesLoaded: false,
      invoices: [],
      customer: {},
      upcomingInvoices: []
    }
  },

  mixins: [page],

  props: {
    companyId: String
  },

  components: {
    PageHeader,
    PageSummary,
    'table-data': Table
  },

  computed: {
    ...mapState(['currentUser']),

    invoiceData() {
      return (date) => {
        return moment.unix(date).format('dddd D MMMM YYYY')
      }
    },

    customerBalance() {
      if (this.positiveBalance) {
        return (this.customer.balance / 100).toString().replace('-', '£')
      } else {
        return `£${this.customer.balance / 100}`
      }
    },

    amountDue() {
      return (invoice) => {
        return `£${invoice.amount_due / 100}`
      }
    },

    hasInvoices() {
      return !!this.upcomingInvoices.length || !!this.invoices.length
    },

    invoiceSite() {
      return (invoice) => {
        return invoice.lines.data[0].metadata.site_name || '–'
      }
    }
  },

  methods: {
    getData() {
      let limit = 500

      readStripeCustomerData(this.companyId)
        .then(({data}) => {
          this.customer = data
          this.hasBalance = data.balance !== 0
          this.positiveBalance = data.balance < 0
        })
        .catch(({response}) => {})

      readCustomerInvoicesData(this.companyId, limit)
        .then(({data}) => {
          this.invoices = data.data
          this.invoicesLoaded = true
        })
        .catch(({response}) => {})

      readCustomerUpcomingInvoicesData(this.companyId)
        .then(({data}) => {
          this.upcomingInvoices = []
          this.upcomingInvoices.push(data)
        })
        .catch(({response}) => {})
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    '$route'() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

.page-invoices
  @extend %page

  .account-balance
    .positive-balance
      strong
        color: #20821d

    .negative-balance
      strong
        color: #d41b1b

  tr
    position: relative

  .row-status
    text-align: center
    border-radius: 3px
    background: #fff
    transition: background .2s ease-in-out
    width: 100%
    pointer-events: none
    text-transform: uppercase
    font-weight: bold
    padding: 2px
    max-width: 50px

  .row-paid
    background: #d3e4d0

  .row-draft
    background: #ffd6b4

  .row-void, .row-open
    background: #fce0de

  .main
    padding: $gap-medium

    > div
      width: 100%

    h2
      margin-top: 0
</style>
